import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { SHEET_TEMPLATES } from "@/lib/sheetTemplates";
import { emptyCells, parseCSV } from "@/lib/sheetEngine";
import { CARD, ICON_BTN } from "@/lib/styles";
import { X, Table2, FilePlus, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function SheetTemplatePicker({ open, folderId = "", onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!open) return null;

  const createSheet = async (title, cells) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/notes", {
        type: "sheet",
        title,
        sheet: { cells },
        folder_id: folderId || null,
      });
      onClose?.();
      navigate(`/hoja/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const cells = parseCSV(text);
      if (!cells.length) { toast.info("El CSV está vacío"); return; }
      const title = file.name.replace(/\.csv$/i, "") || "Hoja importada";
      await createSheet(title, cells);
    } catch {
      toast.error("No se pudo leer el CSV");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose?.()} data-testid="sheet-template-picker">
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-xl flex items-center gap-2">
            <Table2 size={20} className="text-emerald-500" /> Nueva hoja de cálculo
          </h3>
          <button onClick={() => onClose?.()} className={ICON_BTN} data-testid="sheet-template-close"><X size={16} /></button>
        </div>
        <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-5">Empieza en blanco, usa una plantilla o importa un CSV existente.</p>

        {busy && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 rounded-2xl">
            <Loader2 className="animate-spin text-emerald-500" size={26} />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <button onClick={() => createSheet("Nueva hoja de cálculo", emptyCells(12, 6))} className={`${CARD} p-4 text-left flex items-start gap-3 border-dashed`} data-testid="sheet-template-blank">
            <div className="w-10 h-10 rounded-lg bg-slate-500/10 text-slate-500 flex items-center justify-center shrink-0"><FilePlus size={20} /></div>
            <div>
              <p className="font-heading font-medium text-sm">En blanco</p>
              <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Una cuadrícula vacía.</p>
            </div>
          </button>

          <button onClick={() => fileRef.current?.click()} className={`${CARD} p-4 text-left flex items-start gap-3`} data-testid="sheet-template-csv">
            <div className="w-10 h-10 rounded-lg bg-sky-500/10 text-sky-500 flex items-center justify-center shrink-0"><Upload size={20} /></div>
            <div>
              <p className="font-heading font-medium text-sm">Importar CSV</p>
              <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">Convierte un .csv en hoja editable.</p>
            </div>
          </button>

          {SHEET_TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => createSheet(t.name, t.cells.map((r) => r.slice()))} className={`${CARD} p-4 text-left flex items-start gap-3`} data-testid={`sheet-template-${t.id}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0"><Icon size={20} /></div>
                <div>
                  <p className="font-heading font-medium text-sm">{t.name}</p>
                  <p className="font-body text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} data-testid="sheet-csv-input" />
      </div>
    </div>
  );
}
